import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskedLines } from "@/components/motion";
import { CTAButton } from "@/components/CTA";
import IncidentSystem from "@/components/IncidentSystem";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yPanel = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const yWord = useTransform(scrollYProgress, [0, 1], [0, -140]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-screen blueprint-grid overflow-hidden" data-testid="hero">
      <motion.span
        aria-hidden="true"
        style={{ y: yWord, opacity: fade }}
        className="pointer-events-none select-none absolute -bottom-8 left-0 font-oswald font-700 uppercase whitespace-nowrap text-[18vw] leading-none text-outline-faint"
      >
        GESTIONAMOS
      </motion.span>

      <span className="absolute top-28 left-6 lg:left-12 font-mono text-[10px] tracking-[0.3em] text-steel/70 hidden md:block">
        AP.SYS — 01
      </span>
      <span className="absolute top-28 right-6 lg:right-12 font-mono text-[10px] tracking-[0.3em] text-steel/70 hidden md:block">
        RESPUESTA &lt; 24H HÁBILES
      </span>

      <div className="relative mx-auto max-w-[1500px] px-6 lg:px-12 pt-40 lg:pt-48 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
        <motion.div style={{ opacity: fade }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.35 }}
            className="font-mono text-[11px] tracking-[0.4em] text-glow/80 mb-8"
            data-testid="hero-eyebrow"
          >
            GESTIÓN DE INCIDENCIAS — PEQUEÑOS COMERCIOS
          </motion.p>

          <h1 className="font-display font-800 uppercase leading-[0.95] tracking-tight text-[13.5vw] sm:text-7xl lg:text-[6.2rem] text-offwhite">
            <MaskedLines
              mode="load"
              delay={0.45}
              lines={[
                <span key="a">Tu comercio</span>,
                <span key="b">no puede</span>,
                <span key="c" className="font-cormorant normal-case font-medium text-metal">esperar.</span>,
              ]}
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.15, ease: [0.16, 1, 0.3, 1] }}
            className="mt-9 max-w-xl text-mist text-base md:text-lg leading-relaxed"
          >
            Aprea Gestora coordina las incidencias físicas de tu comercio y pone en marcha un técnico
            especializado en menos de <span className="text-silver">24 horas hábiles</span>.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.35, ease: [0.16, 1, 0.3, 1] }}
            className="mt-11 flex flex-wrap items-center gap-5"
          >
            <CTAButton testid="hero-cta-alta">DARME DE ALTA</CTAButton>
            <CTAButton to="/como-funciona" variant="secondary" testid="hero-cta-como-funciona">
              CÓMO FUNCIONA
            </CTAButton>
          </motion.div>
        </motion.div>

        <motion.div style={{ y: yPanel }} className="lg:col-span-5">
          <IncidentSystem />
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3"
      >
        <span className="font-mono text-[10px] tracking-[0.4em] text-steel">SCROLL</span>
        <motion.span
          animate={{ scaleY: [0.3, 1, 0.3] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="w-px h-10 bg-silver/40 origin-top"
        />
      </motion.div>
    </section>
  );
}
